<template>
	<Teleport to="body">
		<Transition name="ac-backdrop">
			<div
				v-if="open"
				class="fixed inset-0 z-[60] bg-black/30"
				@click="store.close()"
			/>
		</Transition>
		<Transition name="ac-panel">
			<aside
				v-if="open"
				class="fixed top-0 right-0 z-[61] h-full w-[360px] max-w-[90vw] bg-white shadow-2xl flex flex-col"
			>
				<header class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
					<span class="font-bold">{{ translate("common.action-center.title") }}</span>
					<button
						type="button"
						class="p-1.5 rounded hover:bg-gray-100"
						aria-label="Sluiten"
						@click="store.close()"
					>
						<AppIcon icon="fa-xmark"/>
					</button>
				</header>

				<div class="flex-1 overflow-y-auto p-4 flex flex-col gap-6 text-sm">
					<!-- Interface language -->
					<section>
						<h3 class="font-bold mb-2">{{ translate("entities.plural.language") }}</h3>
						<div
							v-for="{ label, value } in languageOptions"
							:key="value"
							class="flex flex-row items-center gap-2 rounded p-2 hover:bg-gray-100 cursor-pointer"
							:class="{ 'bg-green-100 hover:bg-green-100 font-bold text-green-600': value == activeLanguageCode }"
							@click="switchLanguage(value)"
						>
							<span class="grow">{{ label }}</span>
						</div>
					</section>

					<!-- Printers (global per category; consumed by the production screens) -->
					<section v-if="$isAllowed('read printer')">
						<h3 class="font-bold mb-2">{{ translate("entities.plural.printer") }}</h3>
						<div class="flex flex-col gap-2">
							<AppPrinterSelect
								v-for="ctrl in visiblePrinterControls"
								:key="ctrl.labelKey"
								:label="translate(ctrl.labelKey)"
								:category="ctrl.category"
								:model-value="store.printerFor(ctrl.category)"
							/>
						</div>
					</section>
				</div>
			</aside>
		</Transition>
	</Teleport>
</template>

<script setup lang="ts">
import { setActiveLanguage, type ReportDesignCategoryEnum } from "@q-food/q-food";
import { loadTranslations, translate } from "~/plugins/I18n.ts";
import { useActionCenterStore } from "~stores/ActionCenter.ts";
import { useAppStore } from "~stores/App.ts";
import { useUserStore } from "~stores/User.ts";

const store = useActionCenterStore();
const { open } = storeToRefs(store);
const { activeLanguageCode } = storeToRefs(useUserStore());

const languageOptions = [
	{ label: "Nederlands", value: "nl" },
	{ label: "English", value: "en" },
];

/**
 * The printer selectors shown in the drawer, one per print category. Selection is global (see
 * ActionCenter store). `visible` is an intentionally-unused seam: it lets a control later be limited to a
 * context (e.g. only on production screens) without restructuring the drawer — today everything is shown
 * whenever the user has `read printer`.
 */
type PrinterControl = { category?: ReportDesignCategoryEnum; labelKey: string; visible?: () => boolean };
const DRAWER_PRINTER_CONTROLS: PrinterControl[] = [
	// No category: the catch-all default printer used when a print has no category-specific preference.
	{ labelKey: "common.action-center.default-printer" },
	{ category: "STOCK_DOCUMENT", labelKey: "common.action-center.lot-printer" },
	{ category: "PRODUCTION_BOX_LABEL", labelKey: "common.action-center.box-printer" },
	{ category: "SALES_WEIGHT_LABEL", labelKey: "common.action-center.weight-printer" },
];
const visiblePrinterControls = computed(() => DRAWER_PRINTER_CONTROLS.filter((control) => control.visible?.() ?? true));

async function switchLanguage(languageCode: string) {
	if (languageCode == activeLanguageCode.value) return;
	await setActiveLanguage(languageCode);
	await loadTranslations(languageCode);
	await useAppStore().refreshApp();
}

function onKeydown(event: KeyboardEvent) {
	if (event.key == "Escape") store.close();
}

onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => window.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
.ac-backdrop-enter-active,
.ac-backdrop-leave-active {
	transition: opacity 0.2s ease;
}

.ac-backdrop-enter-from,
.ac-backdrop-leave-to {
	opacity: 0;
}

.ac-panel-enter-active,
.ac-panel-leave-active {
	transition: transform 0.2s ease;
}

.ac-panel-enter-from,
.ac-panel-leave-to {
	transform: translateX(100%);
}
</style>
